import { Button, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import ConfirmationDialog from 'modules/common/confirmationDialog.component';

/**
 * Toggles a service between cancelled and active, asking the user to confirm before either change is made.
 *
 * Input Props:
 * isCancelled (required): The current state of the service. Decides whether the button cancels or reactivates.
 * onServiceCancelled (required, function): Called once the user confirms cancelling the service.
 * onServiceReactivated (required, function): Called once the user confirms reactivating the service.
 * serviceName: The name of the service to show in the confirmation text, i.e. 'Household Goods'.
 * disabled: Disables the button.
 * sx: Styles for placing the button within its parent. Merged over the button's own styles.
 *
 */

const AuthServiceCancellationToggleButton = props => {
  const { isCancelled, onServiceCancelled, onServiceReactivated, serviceName, disabled, sx } = props;

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  // Held from when the dialog opened so its text doesn't flip while it closes and the service's state changes.
  const [isConfirmingReactivation, setIsConfirmingReactivation] = useState(false);

  const serviceDescription = serviceName ? `the ${serviceName} service` : 'this service';

  const openConfirmation = () => {
    setIsConfirmingReactivation(isCancelled);
    setIsConfirmationOpen(true);
  };

  const handleConfirm = () => {
    setIsConfirmationOpen(false);
    if (isConfirmingReactivation) {
      onServiceReactivated();
      return;
    }
    onServiceCancelled();
  };

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        disabled={disabled}
        sx={{ backgroundColor: '#868686', '&:hover': { backgroundColor: '#474747' }, ...sx }}
        onClick={openConfirmation}
      >
        {isCancelled ? 'Reactivate Service' : 'Cancel Service'}
      </Button>
      <ConfirmationDialog
        open={isConfirmationOpen}
        title={isConfirmingReactivation ? 'Reactivate Service?' : 'Cancel Service?'}
        confirmLabel={isConfirmingReactivation ? 'Reactivate Service' : 'Cancel Service'}
        cancelLabel="Dismiss"
        onConfirm={handleConfirm}
        onCancel={() => setIsConfirmationOpen(false)}
      >
        <Typography>
          Are you sure you want to {isConfirmingReactivation ? 'reactivate' : 'cancel'} {serviceDescription}?
        </Typography>
      </ConfirmationDialog>
    </>
  );
};

AuthServiceCancellationToggleButton.propTypes = {
  isCancelled: PropTypes.bool.isRequired,
  onServiceCancelled: PropTypes.func.isRequired,
  onServiceReactivated: PropTypes.func.isRequired,
  serviceName: PropTypes.string,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default AuthServiceCancellationToggleButton;
